/**
 * O painel "+ Novo colaborador" da aba Outros Bancos.
 *
 * Papel: reproduz `addFavFormHtml` e `incluirFavorecidoNaFolhaGap` (l.
 * 3595–3606, 3723–3737): um cartão sem cabeçalho com o campo "Favorecido
 * cadastrado", a lista dos favorecidos **sem** conta apartada, a dica e
 * "Incluir na folha", que liga a conta apartada de um favorecido que já existe.
 *
 * Como usar: quem monta o põe logo depois do último cartão de folha e decide
 * quando ele aparece por `aberto`; o painel fica montado escondido, para o
 * texto digitado continuar lá quando ele abre de novo, como o `display:none` da
 * origem. Ao abrir, o foco vai para o campo. Depois de incluir, `onIncluido` é
 * chamado, e quem monta fecha o painel, como o redesenho da origem fechava.
 *
 * O nome casa sem diferenciar maiúsculas e sem os espaços das pontas. As duas
 * recusas que a tela decide — campo vazio e nome que não está no cadastro —
 * nunca chegam à API; o nome que já está na folha é recusado aqui e também pela
 * rota, com a mesma frase, que não diz o nome.
 */
import { type Favorecido } from '@concierge/shared';
import { useEffect, useRef, useState } from 'react';

import { useNotificacao } from '@/shared/embutido';
import { useIncluirColaborador } from '../hooks/usar-financas-extras';
import { candidatosAColaborador } from '../outros-bancos';
import { Botao } from './kit/botao';
import { CLASSE_DO_CONTROLE, Campo } from './kit/campo';
import { Cartao, CorpoDoCartao } from './kit/cartao';

interface PainelNovoColaboradorProps {
  /** Os favorecidos da casa, com e sem conta apartada. */
  favorecidos: readonly Favorecido[];
  aberto: boolean;
  /** Chamado depois de a inclusão gravar. */
  onIncluido: () => void;
}

/**
 * O texto de uma falha de gravação.
 *
 * @param erro - O que a mutação lançou.
 * @returns A mensagem da API, que nunca traz dado da família.
 */
function mensagemDe(erro: unknown): string {
  return erro instanceof Error ? erro.message : 'Não foi possível gravar.';
}

/**
 * O painel "+ Novo colaborador".
 *
 * @param favorecidos - Os favorecidos da casa; a lista de sugestões tira os de
 *   conta apartada.
 * @param aberto - Falso esconde o painel sem desmontá-lo.
 * @param onIncluido - Chamado depois da inclusão, para fechar o painel.
 * @returns O cartão do painel.
 */
export function PainelNovoColaborador({
  favorecidos,
  aberto,
  onIncluido,
}: PainelNovoColaboradorProps) {
  const notificar = useNotificacao();
  const incluir = useIncluirColaborador();
  const [nome, setNome] = useState('');
  const campo = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (aberto) campo.current?.focus();
  }, [aberto]);

  /** "Incluir na folha" (l. 3723–3737). */
  async function handleIncluir() {
    const digitado = nome.trim();
    if (digitado === '') {
      notificar('Digite ou selecione o nome de um favorecido já cadastrado.', false);
      return;
    }
    const favorecido = favorecidos.find(
      (candidato) => candidato.nome.trim().toLowerCase() === digitado.toLowerCase(),
    );
    if (favorecido === undefined) {
      notificar(
        'Favorecido não encontrado no cadastro. Cadastre-o primeiro em Cadastros.',
        false,
      );
      return;
    }
    if (favorecido.contaApartada) {
      notificar('Esse nome já está na folha', false);
      setNome('');
      return;
    }
    try {
      await incluir.mutateAsync(favorecido.id);
      setNome('');
      notificar(
        '✓ Incluído na Folha de Pagamento — complete os dados bancários e a ajuda de custo no cadastro dele.',
      );
      onIncluido();
    } catch (erro) {
      notificar(mensagemDe(erro), false);
    }
  }

  return (
    <Cartao hidden={!aberto}>
      <CorpoDoCartao className="px-4 py-3">
        <Campo rotulo="Favorecido cadastrado" id="ob-novo-colaborador">
          <input
            ref={campo}
            id="ob-novo-colaborador"
            type="text"
            list="ob-novo-colaborador-lista"
            placeholder="Digite o nome de um favorecido já cadastrado..."
            autoComplete="off"
            value={nome}
            onChange={(evento) => setNome(evento.target.value)}
            className={CLASSE_DO_CONTROLE}
          />
          <datalist id="ob-novo-colaborador-lista">
            {candidatosAColaborador(favorecidos).map((favorecido) => (
              <option key={favorecido.id} value={favorecido.nome} />
            ))}
          </datalist>
        </Campo>
        <div className="mt-1.5 mb-2.5 text-[11px] text-texto-fraco">
          Passa a aparecer como um cartão de folha de pagamento aqui, com campo de salário
          líquido e (se estiver habilitada no cadastro geral dele) a ajuda de custo. Complete
          os dados bancários e a ajuda de custo no cadastro do favorecido (aba Cadastros) se
          ainda não estiverem lá.
        </div>
        <Botao tamanho="sm" onClick={() => void handleIncluir()} disabled={incluir.isPending}>
          Incluir na folha
        </Botao>
      </CorpoDoCartao>
    </Cartao>
  );
}
